import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useApi } from '../../api/context';
import { queryKeys } from '../../api/queries';
import type { Deployment, DeploymentRequest, Diagnostic } from '../../api/types';
import { usePermission } from '../../auth/context';
import { useToast } from '../../components/toast';
import { ActionButton, Button, Dialog } from '../../components/ui';
import { strings } from '../../strings';
import { RequestStateBadge } from './parts';

const r = strings.networks.requests;

/**
 * The Network card's requests (spec 11 6.15.22): the deployment's requests with the Traffic tab's words and Ask again
 * where `canAskAgain`, and the inbound requests on a target deployment.
 */
export function NetworkCardRequests({ deployment }: { deployment: Deployment }) {
  const network = deployment.network;
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const ids = useId();
  const design = usePermission('design');
  const operate = usePermission('operate');
  const [asking, setAsking] = useState<DeploymentRequest | null>(null);
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!network) return null;
  const requests = network.requests ?? [];
  const inbound = network.inboundRequests ?? [];
  if (!requests.length && !inbound.length) return null;

  const ask = async () => {
    if (!asking) return;
    if (!reason.trim()) {
      setMissing(true);
      return;
    }
    setBusy(true);
    try {
      // The instance's version comes with the Traffic tab's list; the card reads it there before asking.
      const groups = await api.listNetworkRequests(network.id, {}, { limit: 200 });
      const instance = groups.items
        .filter((g) => g.requestId === asking.requestId)
        .flatMap((g) => g.instances)
        .find(
          (i) =>
            i.sourceDeploymentId === asking.sourceDeploymentId &&
            i.provider === asking.provider &&
            i.region === asking.region,
        );
      if (!instance) throw new Error(r.requestChanged);
      const part =
        instance.proposed && ['rejected', 'revoked'].includes(instance.proposed.decision)
          ? 'proposed'
          : 'applied';
      await api.askAgainNetworkRequest(
        network.id,
        {
          sourceDeploymentId: asking.sourceDeploymentId,
          requestId: asking.requestId,
          provider: asking.provider,
          region: asking.region,
        },
        { part, reason: reason.trim() },
        instance.version,
      );
      setAsking(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.deployment(deployment.id) });
      toast.show({ tone: 'success', title: r.confirmAskAgain });
    } catch (error) {
      toast.error(error);
    } finally {
      setBusy(false);
    }
  };

  const target = (x: DeploymentRequest) =>
    x.targetDeploymentName ? `${x.targetDeploymentName}/${x.targetResourceId}` : r.noTarget;
  return (
    <div className="mt-3 space-y-2" data-testid="network-card-requests">
      {requests.length > 0 && (
        <>
          <h3 className="text-[13px] font-semibold">{r.requests}</h3>
          <ul className="space-y-1 text-[13px]">
            {requests.map((x) => (
              <li
                key={`${x.requestId}/${x.provider}/${x.region}`}
                className="flex flex-wrap items-center gap-2"
              >
                <span className="font-mono text-[12.5px]">
                  {x.requestId} → {target(x)}
                </span>
                <RequestStateBadge
                  state={x.state}
                  enforcement={x.enforcement}
                  enforcing={x.enforcing}
                  decision={x.appliedDecision ?? x.proposedDecision}
                />
                {x.canAskAgain && (design || operate) && (
                  <ActionButton
                    permission="read"
                    className="btn-sm"
                    onClick={() => {
                      setReason('');
                      setMissing(false);
                      setAsking(x);
                    }}
                  >
                    {r.askAgain}
                  </ActionButton>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {inbound.length > 0 && (
        <>
          <h3 className="text-[13px] font-semibold">{r.inbound}</h3>
          <ul className="space-y-1 text-[13px]">
            {inbound.map((x) => (
              <li
                key={`in/${x.sourceDeploymentId}/${x.requestId}/${x.region}`}
                className="flex flex-wrap items-center gap-2"
              >
                <span>
                  {r.cardInbound(`${x.sourceDeploymentName}/${x.sourceResourceId}`, x.targetResourceId)}
                </span>
                <RequestStateBadge
                  state={x.state}
                  enforcement={x.enforcement}
                  enforcing={x.enforcing}
                  decision={x.appliedDecision ?? x.proposedDecision}
                />
              </li>
            ))}
          </ul>
        </>
      )}
      {asking && (
        <Dialog
          title={r.askAgainTitle(asking.requestId)}
          onClose={() => setAsking(null)}
          footer={
            <>
              <Button onClick={() => setAsking(null)}>{strings.common.cancel}</Button>
              <Button variant="primary" disabled={busy} onClick={() => void ask()}>
                {r.confirmAskAgain}
              </Button>
            </>
          }
        >
          <p className="mb-3 text-sm">{r.askAgainHelp(network.name)}</p>
          <label htmlFor={`${ids}-reason`} className="label">
            {r.reason}
          </label>
          <textarea
            id={`${ids}-reason`}
            className="input"
            rows={2}
            value={reason}
            aria-invalid={missing || undefined}
            aria-describedby={`${ids}-help`}
            onChange={(e) => {
              setReason(e.target.value);
              setMissing(false);
            }}
          />
          <p id={`${ids}-help`} className={missing ? 'text-[12.5px] text-danger' : 'help'}>
            {missing ? r.reasonMissing : r.reasonRequired}
          </p>
        </Dialog>
      )}
    </div>
  );
}

/**
 * Settings → Network's request targets (spec 11 6.15.22, mock n06): for each ambiguous request a select of its
 * candidates, the suggestion marked; `NETWORK_REQUEST_TARGET_MISMATCH` at the select.
 */
export function RequestTargets({
  choices,
  value,
  disabled,
  diagnostics,
  onChange,
}: {
  choices: NonNullable<import('../../api/types').DeploymentNetworkImpact['requestChoices']>;
  value: Record<string, string>;
  disabled: boolean;
  diagnostics: readonly Diagnostic[];
  onChange: (requestId: string, deploymentId: string) => void;
}) {
  const ids = useId();
  if (!choices.length) return null;
  return (
    <div className="mt-4" data-testid="request-targets">
      <h3 className="mb-1 text-sm font-semibold">{r.networkRequestsOf}</h3>
      <ul className="space-y-2">
        {choices.map((choice) => {
          const id = `${ids}-${choice.requestId}`;
          const messages = diagnostics.filter(
            (d) => d.path === `/settings/network/requestTargets/${choice.requestId}`,
          );
          const chosen = value[choice.requestId] ?? '';
          return (
            <li key={choice.requestId} className="flex flex-wrap items-center gap-2 text-sm">
              <label htmlFor={id} className="font-mono">
                {r.targetLabel(choice.requestId)}
              </label>
              <select
                id={id}
                className="input max-w-xs"
                value={chosen}
                disabled={disabled}
                aria-describedby={`${id}-help`}
                aria-invalid={messages.some((d) => d.severity === 'error') || undefined}
                onChange={(e) => onChange(choice.requestId, e.target.value)}
              >
                <option value="">{r.chooseTarget}</option>
                {choice.candidates.map((c) => (
                  <option key={c.deploymentId} value={c.deploymentId}>
                    {c.name}
                    {c.deploymentId === choice.suggested ? ` (${r.suggested})` : ''}
                  </option>
                ))}
              </select>
              <span id={`${id}-help`} className="text-[12.5px] text-fg-muted">
                {chosen && chosen === choice.suggested ? r.suggestedHelp : ''}
                {messages.map((d) => d.message).join(' ')}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
